import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGate } from 'statsig-react';
import { twMerge } from 'tailwind-merge';
import {
  faCcAmex,
  faCcApplePay,
  faCcVisa,
} from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { SUPER_MINTER_ADDRESS } from '@soundxyz/tiered-sdk-viem/abi/super-minter';
import { getChainName, NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { GooglePay } from '@components/icons/GooglePay';
import { MasterCard } from '@components/icons/MasterCard';
import type { ButtonProps } from '@components/main-components/button/Button';
import { Button } from '@components/main-components/button/Button';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { PayEditionWithCreditCardDocument, useQuery } from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useLogError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { type SuperMinterScheduleWithTier, useTieredMintPrice } from '@store/sdk/tieredReactQuery';
import { FEATURE_GATES } from '@utils/constants';
import type { PlaylistReferral } from '@utils/types';
import { PaperCCMintModal } from './Paper/CCMintModal';
import { usePaperTierCheckout } from './Paper/usePaperTierCheckout';

export type PayWithCCSource = 'Collect Modal' | 'Insufficient Funds Modal';

function PayWithCreditCard({
  onClick,
  disabled,
  loading,
  className,
  variant,
}: {
  className?: string;
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  variant: ButtonProps['variant'];
}) {
  return (
    <Button
      trailingIcon={
        <div className="flex items-center justify-center gap-2">
          {/* @ts-expect-error this is a WegetChainName, NULL_ADDRESSt.org/blog/8182/introducing-the-payment-request-api-for-apple-pay/  */}
          {window?.ApplePaySession && ApplePaySession?.canMakePayments() ? (
            <FontAwesomeIcon icon={faCcApplePay} fontSize={24} />
          ) : (
            <div className="-mr-2">
              <GooglePay width={60} height={60} />
            </div>
          )}
          <FontAwesomeIcon icon={faCcVisa} fontSize={24} className="text-[#0E4595]" />
          <div className="rounded-sm bg-[#231F20]">
            <MasterCard width={24} height={22} />
          </div>
          <FontAwesomeIcon icon={faCcAmex} fontSize={24} className="text-[#2557D6]" />
        </div>
      }
      disabled={disabled}
      loading={loading}
      label="Pay with Card"
      variant={variant}
      size="XL"
      fullWidth
      className={twMerge(loading ? '' : 'justify-between', className)}
      onClick={onClick}
    />
  );
}

export function PayTieredEditionWithCreditCard({
  releaseId,
  editionAddress,
  activeSchedule,
  chainId,
  quantity,
  canCollectSafely,
  disabled,
  className,
  variant,
  source,
  playlistReferral,
}: {
  releaseId: string;
  editionAddress: string;
  activeSchedule: SuperMinterScheduleWithTier;
  chainId: number;
  quantity: number;
  canCollectSafely: boolean;
  disabled: boolean;
  className?: string;
  variant: ButtonProps['variant'];
  /** This is important for analytics. Helps us figure out what place user clicked from  */
  source: PayWithCCSource;
  playlistReferral: PlaylistReferral | null;
}) {
  const logError = useLogError();
  const { value: paperCCMintCheckout } = useGate(FEATURE_GATES.MINTERS_CREDIT_CARD_CHECKOUT);

  const { totalMintPriceWei } = useTieredMintPrice({
    chainId,
    editionAddress,
    mintSchedule: activeSchedule,
    quantity,
  });

  const totalMintPrice = totalMintPriceWei?.total;

  const { getPaperKey, isLoading: creditCardButtonIsLoading } = usePaperTierCheckout({
    releaseId,
    buyPriceWei: totalMintPrice || 0n,
    chain: getChainName(chainId),
    editionAddress,
    scheduleNumber: activeSchedule.scheduleNum,
    tierNumber: activeSchedule.tier,
    quantity,
    type: 'CREDIT_CARD',
  });

  const { openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();

  const { data } = useQuery(PayEditionWithCreditCardDocument, {
    variables: {
      releaseId,
    },
    staleTime: 0,
  });

  const release = data?.data.release;

  const { getAffiliate } = useCheckReferrableRelease({
    release,
  });

  const creditCardLoading = !canCollectSafely || creditCardButtonIsLoading;

  // allow disabling the button if paper is having issues
  if (paperCCMintCheckout) {
    return (
      <PayWithCreditCard
        disabled={disabled || totalMintPrice === 0n}
        loading={creditCardLoading}
        className={className}
        variant={variant}
        onClick={async () => {
          if (!release) return;

          const affiliate = getAffiliate({ playlistReferral }) || NULL_ADDRESS;

          const paperKey = await getPaperKey({ affiliate });

          if ('key' in paperKey) {
            const minterAddress = SUPER_MINTER_ADDRESS;
            const affiliateAddress = affiliate.toLowerCase();
            trackClickEvent({
              event: 'Pay with Credit Card Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent: source,
                minterAddress,
                affiliateAddress,
              },
            });
            openModal(ModalType.PAPER_CC_MINT_MODAL, {
              body: (
                <PaperCCMintModal
                  selectedTier={activeSchedule.tier}
                  paperKey={paperKey.key}
                  quantity={quantity}
                  data={release}
                  onSuccessfulTransaction={() => {
                    trackClickEvent({
                      event: 'Pay with Credit Card Success',
                      properties: {
                        associationId: releaseId,
                        associationType: 'Release',
                        sourceComponent: source,
                        minterAddress,
                        affiliateAddress,
                      },
                    });
                  }}
                  playlistReferral={playlistReferral}
                />
              ),
            });
          } else if ('error' in paperKey) {
            openToast({
              text: paperKey.error,
              variant: 'error',
            });
          } else {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: new Error('Error fetching paper key for PayEditionWithCreditCard'),
              closeProcessingModal: true,
              indexedTags: {
                chainId,
                releaseId,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'fatal',
              message: 'Error fetching paper key for PayEditionWithCreditCard',
              toast: 'Something went wrong. Please try again.',
            });
          }
        }}
      />
    );
  }

  // No credit card provider is enabled
  return null;
}
